import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { api } from '../services/api-client.js';
import { ProductCard } from '../components/ProductCard.jsx';
import { LoadingSpinner } from '../components/LoadingSpinner.jsx';
import { ErrorMessage } from '../components/ErrorMessage.jsx';

export function CatalogPage() {
  const { language, t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters State from URL
  const selectedCategory = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const inStock = searchParams.get('in_stock') === 'true';
  const onSale = searchParams.get('on_sale') === 'true';
  const sortBy = searchParams.get('sort_by') || 'date';
  const page = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    async function loadCatalog() {
      try {
        const [prodRes, catRes] = await Promise.all([
          api.getProducts({
            page,
            perPage: 12,
            category: selectedCategory || undefined,
            search: search || undefined,
            in_stock: inStock || undefined,
            on_sale: onSale || undefined,
            sort_by: sortBy,
          }),
          api.getCategories(),
        ]);

        if (isMounted) {
          setProducts(prodRes.data || []);
          setPaginationMeta(prodRes.meta?.pagination || null);
          setCategories(catRes.data || []);
        }
      } catch (err) {
        if (isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadCatalog();

    return () => {
      isMounted = false;
    };
  }, [selectedCategory, search, inStock, onSale, sortBy, page]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value !== undefined && value !== null && value !== '' && value !== false) {
      next.set(key, String(value));
    } else {
      next.delete(key);
    }
    // Reset page to 1 when changing filters
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  return (
    <div className="container catalog-page">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ marginBottom: '0.5rem' }}>{t('catalog')}</h1>
        <p>{t('heroSubtitle')}</p>
      </div>

      {/* Toolbar / Filters */}
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.25rem',
          marginBottom: '2rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Search */}
        <div style={{ flex: '1 1 240px' }}>
          <input
            type="text"
            placeholder={`🔍 ${t('quickSearch')}...`}
            value={search}
            onChange={(e) => updateParam('search', e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.875rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-bg)',
            }}
          />
        </div>

        {/* Categories Dropdown */}
        <div style={{ flex: '0 1 200px' }}>
          <select
            value={selectedCategory}
            onChange={(e) => updateParam('category', e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-bg)',
            }}
          >
            <option value="">{t('allCategories')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>
                {language === 'ar' ? c.name_ar : c.name_fr}
              </option>
            ))}
          </select>
        </div>

        {/* Checkboxes & Sort */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={inStock}
              onChange={(e) => updateParam('in_stock', e.target.checked)}
            />
            <span>{t('inStockOnly')}</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={onSale}
              onChange={(e) => updateParam('on_sale', e.target.checked)}
            />
            <span>{t('onSaleOnly')}</span>
          </label>

          <select
            value={sortBy}
            onChange={(e) => updateParam('sort_by', e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-bg)',
              fontSize: '0.875rem',
            }}
          >
            <option value="date">{t('sortNewest')}</option>
            <option value="price">{t('sortPriceAsc')}</option>
          </select>
        </div>
      </div>

      {/* Main Results */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => window.location.reload()} />
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 1rem', backgroundColor: 'var(--color-surface)', borderRadius: 'var(--radius-lg)' }}>
          <p style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>{t('noProductsFound')}</p>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setSearchParams(new URLSearchParams())}
          >
            Réinitialiser les filtres
          </button>
        </div>
      ) : (
        <>
          <div className="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Pagination */}
          {paginationMeta && paginationMeta.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '3rem' }}>
              <button
                type="button"
                className="btn btn-outline"
                disabled={!paginationMeta.hasPrevPage}
                onClick={() => updateParam('page', page - 1)}
              >
                ← {t('previous')}
              </button>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                {t('page')} {paginationMeta.page} {t('of')} {paginationMeta.totalPages}
              </span>
              <button
                type="button"
                className="btn btn-outline"
                disabled={!paginationMeta.hasNextPage}
                onClick={() => updateParam('page', page + 1)}
              >
                {t('next')} →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
